import { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Slider from 'react-slick';
import { Dialog } from '@mui/material';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import * as S from './Timetable.styles';
import { fetchAllTimetables } from '../../api/timetablePublicApi';

const campuses = [
  { id: 1, name: 'Victoria Park' },
  { id: 2, name: 'Claremont' },
  { id: 3, name: 'Osborne Park' },
];

const Timetable = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [errorImages, setErrorImages] = useState({});
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [showInfo, setShowInfo] = useState(false); // 控制信息提示显示
  const [showHelpTip, setShowHelpTip] = useState(false); // 控制操作提示显示
  const [showNavButtons, setShowNavButtons] = useState(false); // 控制导航按钮显示
  const [cacheKey, setCacheKey] = useState(Date.now());
  const [campusTimetables, setCampusTimetables] = useState({}); // { campusName: [url, ...] }
  const [isLoading, setIsLoading] = useState(true);
  const selectorRef = useRef(null);
  const sliderRef = useRef(null);
  const infoTimeoutRef = useRef(null);
  const helpTipTimeoutRef = useRef(null);
  const navButtonTimeoutRef = useRef(null);

  // 从API加载所有校区的时间表
  const loadTimetables = useCallback(async () => {
    setIsLoading(true);
    try {
      const timetables = await fetchAllTimetables();
      setCampusTimetables(timetables);
    } catch (error) {
      console.error('Failed to fetch timetables:', error);
      setCampusTimetables({});
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadTimetables();
  }, [loadTimetables]);

  // 构建所有时间表的数组，包含校区信息
  const allTimetables = [];
  const campusStartIndices = {}; // 记录每个校区的开始索引
  
  if (!isLoading) {
    campuses.forEach((campus, campusIndex) => {
      const images = campusTimetables[campus.name] || [];
      if (images.length > 0) {
        campusStartIndices[campusIndex] = allTimetables.length;
        images.forEach((imageUrl, timetableIndex) => {
          allTimetables.push({
            campusIndex,
            campusName: campus.name,
            imageUrl: `${imageUrl}?v=${cacheKey}`,
            isFirstInCampus: timetableIndex === 0
          });
        });
      }
    });
  }

  // 根据当前幻灯片确定选中的校区
  const selectedCampusIndex = allTimetables[currentSlide]?.campusIndex || 0;

  // 显示信息提示并设置自动隐藏
  const showInfoWithTimeout = () => {
    setShowInfo(true);
    
    // 清除之前的定时器
    if (infoTimeoutRef.current) {
      clearTimeout(infoTimeoutRef.current);
    }
    
    // 设置2秒后隐藏
    infoTimeoutRef.current = setTimeout(() => {
      setShowInfo(false);
    }, 2000);
  };

  // 显示操作提示
  const showHelpTipWithTimeout = () => {
    setShowHelpTip(true);
    
    // 清除之前的定时器
    if (helpTipTimeoutRef.current) {
      clearTimeout(helpTipTimeoutRef.current);
    }
    
    // 设置2秒后隐藏
    helpTipTimeoutRef.current = setTimeout(() => {
      setShowHelpTip(false);
    }, 2000);
  };

  // 处理导航按钮区域的鼠标事件
  const handleNavAreaMouseEnter = () => {
    if (allTimetables.length > 1) {
      setShowNavButtons(true);
      if (navButtonTimeoutRef.current) {
        clearTimeout(navButtonTimeoutRef.current);
      }
    }
  };

  const handleNavAreaMouseLeave = () => {
    if (navButtonTimeoutRef.current) {
      clearTimeout(navButtonTimeoutRef.current);
    }
    navButtonTimeoutRef.current = setTimeout(() => {
      setShowNavButtons(false);
    }, 200); // 减少延迟，让按钮更快隐藏
  };

  // 组件挂载时显示操作提示
  useEffect(() => {
    showHelpTipWithTimeout();
  }, []);

  // 滚动到选中校区按钮（移动端）
  useEffect(() => {
    if (selectorRef.current && window.innerWidth <= 768) {
      const activeButton = selectorRef.current.children[selectedCampusIndex];
      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [selectedCampusIndex]);

  // 轮播设置
  const sliderSettings = {
    dots: allTimetables.length > 1,
    infinite: allTimetables.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    adaptiveHeight: false,
    swipe: true,
    touchMove: true,
    dotsClass: 'custom-dots',
    afterChange: (index) => {
      setCurrentSlide(index);
      showInfoWithTimeout(); // 页码变化时显示信息
    },
    customPaging: (i) => {
      return (
        <div className="custom-dot">
          <span></span>
        </div>
      );
    }
  };

  const handleImageLoad = (imageUrl) => {
    setLoadedImages(prev => ({ ...prev, [imageUrl]: true }));
  };

  const handleImageError = (imageUrl) => {
    setErrorImages(prev => ({ ...prev, [imageUrl]: true }));
  };

  const handleImageClick = (imageUrl) => {
    setEnlargedImage(imageUrl);
  };

  // 点击校区按钮跳转到该校区的第一张时间表
  const handleCampusClick = (campusIndex) => {
    const targetIndex = campusStartIndices[campusIndex];
    if (sliderRef.current && targetIndex !== undefined) {
      sliderRef.current.slickGoTo(targetIndex);
    }
  };

  // 控制滑块导航
  const goToPrevSlide = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const goToNextSlide = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };
  
  // 强制刷新图片，清除缓存
  const refreshTimetables = () => {
    setCacheKey(Date.now());
    setLoadedImages({});
    setErrorImages({});
    loadTimetables();
    setShowInfo(true);
    if (infoTimeoutRef.current) {
      clearTimeout(infoTimeoutRef.current);
    }
    infoTimeoutRef.current = setTimeout(() => {
      setShowInfo(false);
    }, 2000);
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (infoTimeoutRef.current) {
        clearTimeout(infoTimeoutRef.current);
      }
      if (helpTipTimeoutRef.current) {
        clearTimeout(helpTipTimeoutRef.current);
      }
      if (navButtonTimeoutRef.current) {
        clearTimeout(navButtonTimeoutRef.current);
      }
    };
  }, []);

  return (
    <S.PageContainer>
      <Helmet>
        <title>Uplus Education - Timetable</title>
        <meta name="description" content="View our campus timetables" />
      </Helmet>
      
      {/* 校区选择器 */}
      <S.TopCampusSelector ref={selectorRef}>
        {campuses.map((campus, index) => (
          <S.CampusButton
            key={campus.id}
            isActive={index === selectedCampusIndex}
            onClick={() => handleCampusClick(index)}
          >
            {campus.name}
            <S.TimetableCount>
              ({isLoading ? '...' : (campusTimetables[campus.name]?.length || 0)})
            </S.TimetableCount>
          </S.CampusButton>
        ))}
        <S.RefreshButton onClick={refreshTimetables} title="Refresh Timetables">
          🔄
        </S.RefreshButton>
      </S.TopCampusSelector>
      
      <S.MainContent>
        <S.TimetableDisplay>
          {isLoading ? (
            <S.LoadingPlaceholder>Loading timetables...</S.LoadingPlaceholder>
          ) : allTimetables.length > 0 ? (
            <S.SliderContainer>
              {/* 左侧导航区域 */}
              {allTimetables.length > 1 && (
                <S.NavArea 
                  direction="left"
                  onMouseEnter={handleNavAreaMouseEnter}
                  onMouseLeave={handleNavAreaMouseLeave}
                >
                  <S.NavButton 
                    direction="left" 
                    onClick={goToPrevSlide}
                    isVisible={showNavButtons}
                  >
                    &#9664;
                  </S.NavButton>
                </S.NavArea>
              )}

              {/* 右侧导航区域 */}
              {allTimetables.length > 1 && (
                <S.NavArea 
                  direction="right"
                  onMouseEnter={handleNavAreaMouseEnter}
                  onMouseLeave={handleNavAreaMouseLeave}
                >
                  <S.NavButton 
                    direction="right" 
                    onClick={goToNextSlide}
                    isVisible={showNavButtons}
                  >
                    &#9654;
                  </S.NavButton>
                </S.NavArea>
              )}
              
              {/* 当前校区和时间表信息 - 只在变化时显示 */}
              <S.CurrentInfo isVisible={showInfo}>
                {allTimetables[currentSlide]?.campusName} - Timetable {
                  currentSlide - campusStartIndices[selectedCampusIndex] + 1
                } of {campusTimetables[campuses[selectedCampusIndex]?.name]?.length || 1}
              </S.CurrentInfo>

              {/* 操作提示 */}
              <S.HelpTip isVisible={showHelpTip}>
                💡 Swipe left/right to browse • Click to enlarge
              </S.HelpTip>
              
              <Slider {...sliderSettings} ref={sliderRef}>
                {allTimetables.map((item, index) => (
                  <div key={index}>
                    <S.TimetableImage>
                      {!loadedImages[item.imageUrl] && !errorImages[item.imageUrl] && (
                        <S.LoadingPlaceholder />
                      )}
                      {errorImages[item.imageUrl] ? (
                        <S.ErrorPlaceholder>
                          {item.campusName} - Timetable not available
                        </S.ErrorPlaceholder>
                      ) : (
                        <img
                          src={item.imageUrl}
                          alt={`${item.campusName} Timetable`}
                          onLoad={() => handleImageLoad(item.imageUrl)}
                          onError={() => handleImageError(item.imageUrl)}
                          onClick={() => handleImageClick(item.imageUrl)}
                          style={{
                            opacity: loadedImages[item.imageUrl] ? 1 : 0,
                            transition: 'opacity 0.3s ease-in-out',
                            cursor: 'pointer',
                            position: loadedImages[item.imageUrl] ? 'static' : 'absolute',
                            top: loadedImages[item.imageUrl] ? 'auto' : '0',
                            left: loadedImages[item.imageUrl] ? 'auto' : '0',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      )}
                    </S.TimetableImage>
                  </div>
                ))}
              </Slider>
            </S.SliderContainer>
          ) : (
            <S.ErrorPlaceholder>
              No timetables available
            </S.ErrorPlaceholder>
          )}
        </S.TimetableDisplay>
      </S.MainContent>

      {/* 放大图片对话框 */}
      <Dialog 
        open={enlargedImage !== null} 
        onClose={() => setEnlargedImage(null)} 
        maxWidth={false}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: '95vw',
            maxHeight: '95vh'
          }
        }}
      >
        {enlargedImage && (
          <img
            src={enlargedImage}
            alt="Enlarged Timetable"
            style={{ 
              maxWidth: '95vw', 
              maxHeight: '95vh', 
              display: 'block', 
              margin: 'auto',
              cursor: 'pointer'
            }}
            onClick={() => setEnlargedImage(null)}
          />
        )}
      </Dialog>
    </S.PageContainer>
  );
};

export default Timetable;